import { Component, OnInit, inject, signal } from '@angular/core';
import {CookieService} from '../../service/cookie.service';
import {ConfigurationHelper} from '../../helper/configuration.helper';
import {ApplicationService} from '../../service/application.service';

/**
 * Cookie banner component with methods for accepting cookies
 *
 * The following cookie levels are accepted
 * - 0: Do not agree and hide cookie banner
 * - 1: Agree to the function cookies only this one is always accepted and do not necessarily need consent
 * - 2: Agree to the Preference cookies plus cookies for level 1 (for example choices you've made like filters, fast checkout)
 * - 3: Agree to the Statistical cookies plus cookies for level 1 (for example Analytics and performance testing cookies)
 * - 4: Agree to the Marketing cookies plus cookies for level 1 (for example Adwords or any other company that uses information from the cookie)
 *
 * Level 1 is always accepted since those are currently not read from tag manager. This code only prepares the site/shop for multi level
 * cookie consent. At the moment this always accepts to Level 3 (Statistical cookies)
 */
@Component({
	selector: 'cookie-banner',
	templateUrl: 'cookie.banner.component.html',
	styleUrls: ['cookie.banner.component.scss'],
	standalone: true
})
export class CookieBannerComponent implements OnInit {
	private readonly cookieService = inject(CookieService);
	protected applicationService = inject(ApplicationService);


	static readonly ACCEPTED_COOKIES: string = 'acceptedCookies';

	static readonly ACCEPT_ESSENTIAL_COOKIES_NAME: string = 'essentialCookiesAccepted';

	static readonly ACCEPT_ANALYTICS_COOKIES_NAME: string = 'analyticsCookiesAccepted';

	static readonly ACCEPT_MARKETING_COOKIES_NAME: string = 'marketingCookiesAccepted';

	static readonly HIDE_COOKIE_BANNER_NAME: string = 'hideCookieBanner';

	readonly cookieStatementUrl = signal<string>(undefined);

	constructor() {
		const cookieService = this.cookieService;
		const applicationService = this.applicationService;

		this.cookieService = cookieService;
		this.applicationService = applicationService;
	}

	ngOnInit(): void {
		this.cookieService.getCookiePolicyUrl()
			.then((response: any) => {
				this.cookieStatementUrl.set(`${ConfigurationHelper.getWebsiteUrl(response)}`);
			});
	}

	getCookieStatementUrl(): string {
		return this.cookieStatementUrl();
	}

	/**
	 * Sets the functional cookie acceptance.
	 * This will allow for example setting user cookies for login or shopping carts etc.
	 */
	private agreeToEssentialCookies(): void {
		this.cookieService.setCookieByName(CookieBannerComponent.ACCEPTED_COOKIES, true);
		this.cookieService.setCookieByName(CookieBannerComponent.ACCEPT_ESSENTIAL_COOKIES_NAME, true);
	}

	/**
	 * Sets the statistical cookie acceptance.
	 * This will allow for instance analytics cookies.
	 */

	private agreeToAnalyticsCookies(): void {
		this.cookieService.setCookieByName(CookieBannerComponent.ACCEPT_ANALYTICS_COOKIES_NAME, true);
	}

	/**
	 * Sets the marketing cookie acceptance.
	 * This will allow for example setting a cookie for adwords.
	 */
	private agreeToMarketingCookies(): void {
		this.cookieService.setCookieByName(CookieBannerComponent.ACCEPT_MARKETING_COOKIES_NAME, true);
	}

	acceptAllCookies(): void {
		this.agreeToEssentialCookies();
		this.agreeToAnalyticsCookies();
		this.agreeToMarketingCookies();
	}
}
